import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomerRepresentativeFilters, OrderFilters, OrdersState } from './types';
import { initialExecutorsPageFilters, initialFilters } from 'pages/Orders/constants';
import { isEmptyObject } from 'utils/helpers';
import { MyExecutorsFiltersFormValues } from 'pages/Executors/MyExecutors/MyExecutorsFiltersForm/types';
import { InvitationFiltersFormValues } from 'pages/Executors/Invites/InvitationFiltersForm/types';
import { initialInvitationFormFilters } from 'pages/Executors/Invites/InvitationFiltersForm/const';

const getInitialFilters = (): OrderFilters => {
  // Проверяем sessionStorage
  const storedFilters = sessionStorage.getItem('filters');

  if (storedFilters !== null) {
    try {
      // Пытаемся распарсить сохранённые фильтры
      const parsedFilters = JSON.parse(storedFilters) as OrderFilters;
      // Возвращаем сохранённое значение, проверяя структуру
      return {
        cities: Array.isArray(parsedFilters.cities) ? parsedFilters.cities : [],
        customId: typeof parsedFilters.customId === 'string' ? parsedFilters.customId : '',
        create_date:
          parsedFilters.create_date &&
          typeof parsedFilters.create_date === 'object' &&
          !isEmptyObject(parsedFilters.create_date)
            ? {
                ...parsedFilters.create_date,
                startDate: new Date(parsedFilters.create_date.startDate ?? ''),
                endDate: new Date(parsedFilters.create_date.endDate ?? ''),
              }
            : {},
        clients: Array.isArray(parsedFilters.clients) ? parsedFilters.clients : [],
        areDependentObjectsIncluded:
          typeof parsedFilters.areDependentObjectsIncluded === 'boolean'
            ? parsedFilters.areDependentObjectsIncluded
            : false,
      };
    } catch (e) {
      // В случае ошибки парсинга возвращаем объект по умолчанию
      console.error('Failed to parse filters from sessionStorage', e);
    }
  }

  return initialFilters;
};

const getInitialCustomerRepresentativeFilters = (): CustomerRepresentativeFilters => {
  // Проверяем sessionStorage
  const storedFilters = sessionStorage.getItem('customerRepresentativeFilters');

  if (storedFilters !== null) {
    try {
      // Пытаемся распарсить сохранённые фильтры
      const parsedFilters = JSON.parse(storedFilters) as CustomerRepresentativeFilters;
      // Возвращаем сохранённое значение, проверяя структуру
      return {
        initiators: Array.isArray(parsedFilters.initiators) ? parsedFilters.initiators : [],
      };
    } catch (e) {
      // В случае ошибки парсинга возвращаем объект по умолчанию
      console.error('Failed to parse filters from sessionStorage', e);
    }
  }

  return { initiators: [] };
};

const getExecutorsPageFilters = (): MyExecutorsFiltersFormValues => {
  // Проверяем sessionStorage
  const storedFilters = sessionStorage.getItem('executorsPageFilters');

  if (storedFilters !== null) {
    try {
      // Пытаемся распарсить сохранённые фильтры
      const parsedFilters = JSON.parse(storedFilters) as MyExecutorsFiltersFormValues;
      // Возвращаем сохранённое значение, проверяя структуру
      return {
        fullName: typeof parsedFilters.fullName === 'string' ? parsedFilters.fullName : '',
        tin: typeof parsedFilters.tin === 'string' ? parsedFilters.tin : '',
        ftsStatusName: Array.isArray(parsedFilters.ftsStatusName)
          ? parsedFilters.ftsStatusName
          : [],
      };
    } catch (e) {
      // В случае ошибки парсинга возвращаем объект по умолчанию
      console.error('Failed to parse filters from sessionStorage', e);
    }
  }

  return initialExecutorsPageFilters;
};

const getInvitationPageFilters = (): InvitationFiltersFormValues => {
  // Проверяем sessionStorage
  const storedFilters = sessionStorage.getItem('invitationPageFilters');

  if (storedFilters !== null) {
    try {
      // Пытаемся распарсить сохранённые фильтры
      const parsedFilters = JSON.parse(storedFilters) as InvitationFiltersFormValues;
      // Возвращаем сохранённое значение, проверяя структуру
      return {
        createDate:
          parsedFilters.createDate &&
          typeof parsedFilters.createDate === 'object' &&
          !isEmptyObject(parsedFilters.createDate)
            ? {
                ...parsedFilters.createDate,
                startDate: new Date(parsedFilters.createDate.startDate ?? ''),
                endDate: new Date(parsedFilters.createDate.endDate ?? ''),
              }
            : {},
        fullName: typeof parsedFilters.fullName === 'string' ? parsedFilters.fullName : '',
        email: typeof parsedFilters.email === 'string' ? parsedFilters.email : '',
        statuses: Array.isArray(parsedFilters.statuses) ? parsedFilters.statuses : [],
      };
    } catch (e) {
      // В случае ошибки парсинга возвращаем объект по умолчанию
      console.error('Failed to parse filters from sessionStorage', e);
    }
  }

  return initialInvitationFormFilters;
};

const initialState: OrdersState = {
  filters: getInitialFilters(),
  customerRepresentativeFilters: getInitialCustomerRepresentativeFilters(),
  executorsPageFilters: getExecutorsPageFilters(),
  invitationPageFilters: getInvitationPageFilters(),
};

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<OrderFilters>) => {
      sessionStorage.setItem('filters', JSON.stringify(action.payload));
      state.filters = action.payload;
    },
    setCustomerRepresentativeFilters: (
      state,
      action: PayloadAction<CustomerRepresentativeFilters>
    ) => {
      sessionStorage.setItem('customerRepresentativeFilters', JSON.stringify(action.payload));
      state.customerRepresentativeFilters = action.payload;
    },
    setExecutorsPageFilters: (state, action: PayloadAction<MyExecutorsFiltersFormValues>) => {
      sessionStorage.setItem('executorsPageFilters', JSON.stringify(action.payload));
      state.executorsPageFilters = action.payload;
    },
    setInvitationPageFilters: (state, action: PayloadAction<InvitationFiltersFormValues>) => {
      sessionStorage.setItem('invitationPageFilters', JSON.stringify(action.payload));
      state.invitationPageFilters = action.payload;
    },
  },
});

export const { reducer: ordersReducer, actions: ordersActions } = ordersSlice;
